import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Creatable,
  Drawer,
  Input,
  Switch,
  useDrawer,
} from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { JsonInput } from '@/common/components/JsonInput'
import { useRemoveApplication } from '../hooks/useRemoveApplication'
import {
  ViewApplicationForm,
  ViewApplicationSchema,
} from '../schemas/ViewApplicationSchema'
import { useSelectedApplicationStore } from '../stores/useSelectedApplicationStore'

export function DrawerViewApplication() {
  const { t } = useTranslation(['sso', 'common'])
  const { close, open } = useDrawer('drawer-view-application')
  const { application } = useSelectedApplicationStore()
  const { handleRemoveApplication } = useRemoveApplication()

  const hookForm = useForm<ViewApplicationForm>({
    resolver: zodResolver(ViewApplicationSchema()),
    defaultValues: {
      applicationKey: '',
      title: '',
      description: '',
      tag: [],
      auth: '',
      metadata: '',
      needsApiUser: false,
      needsEntityAccount: false,
    },
  })

  const handleClose = () => {
    hookForm.reset()
    close()
  }

  const handleRemove = () => {
    if (application?.id) {
      close()
      handleRemoveApplication(application.id, () => {
        open()
      })
    }
  }

  useEffect(() => {
    if (application) {
      hookForm.reset({
        applicationKey: application.applicationKey || '',
        title: application.title || '',
        description: application.description || '',
        tag: application.tag?.map((tag) => ({ label: tag, value: tag })) || [],
        auth: application.auth
          ? JSON.stringify(application.auth, null, 2)
          : '{}',
        metadata: application.metadata
          ? JSON.stringify(application.metadata, null, 2)
          : '{}',
        needsApiUser: application.requiresApiUser || false,
        needsEntityAccount: application.requiresEntityAccount || false,
      })
    }
  }, [application, hookForm])

  return (
    <Drawer.Root id="drawer-view-application">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:applications.drawer_view_application.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:applications.drawer_view_application.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input.Base
                  name="applicationKey"
                  disabled
                  label={t(
                    'sso:applications.drawer_view_application.fields.application_key.label',
                  )}
                />
              </div>

              <div className="flex-1">
                <Input.Base
                  name="title"
                  disabled
                  label={t(
                    'sso:applications.drawer_view_application.fields.title.label',
                  )}
                />
              </div>
            </div>

            <Input.Base
              name="description"
              disabled
              placeholder={t('common:components.not_informed')}
              label={t(
                'sso:applications.drawer_view_application.fields.description.label',
              )}
            />

            <Creatable.Sync
              name="tag"
              icon={null}
              isMulti
              isDisabled
              label={t(
                'sso:applications.drawer_view_application.fields.tags.label',
              )}
            />

            <JsonInput
              name="auth"
              disabled
              label={t(
                'sso:applications.drawer_view_application.fields.auth_rules.label',
              )}
            />

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="needsApiUser">
                {t(
                  'sso:applications.drawer_view_application.fields.requires_api_user.label',
                )}
              </Switch.Label>
              <Switch.Trigger name="needsApiUser" disabled />
            </Switch.Root>

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="needsEntityAccount">
                {t(
                  'sso:applications.drawer_view_application.fields.requires_entity_account.label',
                )}
              </Switch.Label>
              <Switch.Trigger name="needsEntityAccount" disabled />
            </Switch.Root>

            {application?.metadata != null && (
              <JsonInput
                name="metadata"
                disabled
                label={t(
                  'sso:applications.drawer_view_application.fields.metadata_rules.label',
                )}
              />
            )}
          </div>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-between">
          <Button.Root
            variant="secondary"
            color="danger"
            onClick={handleRemove}
          >
            {t(
              'sso:applications.drawer_view_application.footer.remove_button.label',
            )}
          </Button.Root>

          <Button.Root variant="secondary" color="brand" onClick={handleClose}>
            {t(
              'sso:applications.drawer_view_application.footer.close_button.label',
            )}
          </Button.Root>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
